/**
 * Dev mode: auth bypass + fake session — NOT for real users.
 *
 * Lets the app run with no Stytch keys and no Django backend, so a fresh clone
 * boots straight into the UI and a branch can be built into a working preview.
 *
 * Activation is decided by `isDevAuthBypass()` (see below). Without the flag a
 * production build is unaffected: the bypass stays off and real login applies.
 */
import type { AuthTokens, Label, Organization, User } from "@/types";
import { tokenStorage } from "@/lib/auth";

function isPlaceholder(value: string): boolean {
  return value.trim() === "" || value.includes("xxx");
}

/**
 * Whether to bypass real auth and serve the in-browser mock.
 *
 *  - VITE_DEV_AUTH_BYPASS="true"  → ON in ANY build, including production ones.
 *    This is what a Cloudflare preview deploy sets.
 *  - VITE_DEV_AUTH_BYPASS="false" → OFF (use real Stytch login).
 *  - unset → auto-enable only in `pnpm dev` when Stytch keys are missing or
 *    still the placeholders from .env.sample.
 *
 * ⚠️ Turning the flag on for a publicly reachable build exposes a fake-auth app
 * with mock data and NO real authentication. Prototypes and demos only.
 */
export function isDevAuthBypass(): boolean {
  const flag = (import.meta.env.VITE_DEV_AUTH_BYPASS ?? "").toLowerCase();
  if (flag === "true") return true;
  if (flag === "false") return false;
  // No explicit flag: auto-enable only in dev with missing/placeholder Stytch keys.
  if (!import.meta.env.DEV) return false;
  const token = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN ?? "";
  const org = import.meta.env.VITE_STYTCH_ORGANIZATION_ID ?? "";
  return isPlaceholder(token) || isPlaceholder(org);
}

export const DEV_LABEL: Label = {
  id: "dev-label",
  name: "Universal Music",
  currency: "USD",
};

export const DEV_ORG: Organization = {
  id: "dev-org",
  name: "Dev Organization",
  slug: "dev-org",
  labels: [DEV_LABEL],
  role: "owner",
};

/** Fake current user returned to the app while the bypass is active. */
export const DEV_USER: User = {
  id: "dev-user",
  username: "dev",
  email: "dev@local.test",
  first_name: "Dev",
  last_name: "User",
  // A label user, not staff: the sidebar is the one a label sees, without the staff tools.
  is_staff: false,
  organizations: [DEV_ORG],
  // No payee portal access: the app runs in label mode.
  payees: [],
};

function base64Url(value: object): string {
  return btoa(JSON.stringify(value)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}

// A structurally-valid, non-expired JWT (exp = year 2100) so tokenStorage /
// protectedLoader / isTokenExpired treat the session as live. The signature is
// fake — a real backend would reject it, which is fine for a no-backend run.
function fakeJwt(): string {
  return `${base64Url({ alg: "none", typ: "JWT" })}.${base64Url({ exp: 4102444800, sub: "dev-user" })}.dev`;
}

export const DEV_TOKENS: AuthTokens = {
  access: fakeJwt(),
  refresh: fakeJwt(),
};

/** Seed fake tokens into localStorage so the route guards pass. No-op unless the bypass is active. */
export function seedDevAuth(): void {
  if (!isDevAuthBypass()) return;
  if (tokenStorage.get()) return;
  tokenStorage.set(DEV_TOKENS);
  console.warn("[dev-mode] Auth bypass active — logged in as dev@local.test. No real backend.");
}
